import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, RevealStagger, StaggerItem } from "../components/Reveal";
import { HeroFlow } from "../components/HeroFlow";
import { MagneticButton } from "../components/MagneticButton";
import { Marquee } from "../components/Marquee";
import { FaqList } from "../components/FaqList";
import { FinalCTA } from "../components/FinalCTA";
import { ServiceCard } from "../components/ServiceCard";
import { PillarFramework } from "../components/PillarFramework";
import { WhyInfographic } from "../components/WhyInfographic";
import { ProcessJourney } from "../components/ProcessJourney";
import { CaseStudyVisual } from "../components/CaseStudyVisual";
import { CredibilityStrip } from "../components/CredibilityStrip";
import { FloatingMotif } from "../components/FloatingMotif";
import { RevealStat } from "../components/Counter";
import { SERVICES, TESTIMONIALS, FAQS, CASE_STUDIES } from "../data/site";

const EASE = [0.16, 1, 0.3, 1];

const Section = ({ children, className = "", ...rest }) => (
    <section className={`py-24 md:py-36 ${className}`} {...rest}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">{children}</div>
    </section>
);

const SectionLabel = ({ number, label, variant = "dark" }) => (
    <div className="flex items-center gap-6">
        <span className={`text-[10px] tracking-[0.28em] uppercase ${variant === "light" ? "text-white/60" : "text-[#444444]"}`}>
            {number}
        </span>
        <span className={`h-px w-12 inline-block ${variant === "light" ? "bg-white" : "bg-[#111111]"}`} />
        <span className={`text-[10px] tracking-[0.28em] uppercase font-medium ${variant === "light" ? "text-white" : "text-[#111111]"}`}>
            {label}
        </span>
    </div>
);

export default function Home() {
    return (
        <div data-testid="page-home">
            {/* ─── HERO ─── */}
            <section className="relative pt-8 md:pt-12 pb-20 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 grid-lines-fine opacity-60 pointer-events-none" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
                        <div className="md:col-span-7 md:pr-8">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        Pixemance · Volume 01
                                    </span>
                                    <span className="ml-auto hidden md:flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#111111] animate-pulse" />
                                        Accepting Q1 partners
                                    </span>
                                </div>
                            </Reveal>

                            <Reveal delay={0.05}>
                                <h1
                                    data-testid="hero-headline"
                                    className="font-serif text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-[-0.02em] text-[#111111]"
                                >
                                    Your business
                                    <br />
                                    doesn’t need
                                    <br />
                                    another website.
                                </h1>
                            </Reveal>

                            <Reveal delay={0.15}>
                                <p
                                    data-testid="hero-sub-headline"
                                    className="mt-8 font-serif italic text-2xl md:text-[28px] text-[#444444] leading-snug max-w-2xl"
                                >
                                    It needs a digital presence that builds
                                    trust and generates enquiries.
                                </p>
                            </Reveal>

                            <Reveal delay={0.25}>
                                <p className="mt-8 text-[#444444] text-lg leading-relaxed max-w-xl">
                                    Pixemance builds business-first digital
                                    assets — engineered to strengthen
                                    credibility, improve visibility, and
                                    compound long-term growth.
                                </p>
                            </Reveal>

                            <Reveal delay={0.35}>
                                <div className="mt-12 flex flex-wrap items-center gap-4">
                                    <MagneticButton
                                        to="/contact"
                                        testid="hero-cta-primary"
                                    >
                                        Book a Strategy Call
                                    </MagneticButton>
                                    <MagneticButton
                                        to="/services"
                                        testid="hero-cta-secondary"
                                        variant="outline"
                                    >
                                        View Services
                                    </MagneticButton>
                                </div>
                            </Reveal>

                            <Reveal delay={0.5}>
                                <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 border-t border-[#dddddd] pt-8">
                                    {[
                                        { k: "↳ Approach", v: "Business-first" },
                                        { k: "↳ Position", v: "Premium" },
                                        { k: "↳ Built for", v: "Performance" },
                                    ].map((t) => (
                                        <div key={t.v}>
                                            <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                                {t.k}
                                            </p>
                                            <p className="mt-2 font-serif text-xl text-[#111111] leading-tight">
                                                {t.v}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        </div>

                        <div className="md:col-span-5 md:mt-2 md:sticky md:top-28">
                            <HeroFlow />
                        </div>
                    </div>
                </div>
            </section>

            <CredibilityStrip />

            {/* MARQUEE */}
            <Marquee />

            {/* ─── PROBLEM AGITATION ─── */}
            <Section data-testid="section-problem" className="relative overflow-hidden">
                <FloatingMotif />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 relative">
                    <div className="md:col-span-4">
                        <SectionLabel number="§ 02" label="The Problem" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                Most opportunities
                                <br />
                                <em className="text-[#444444]">die quietly.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal delay={0.05}>
                            <p className="font-serif text-2xl md:text-4xl leading-[1.2] text-[#111111] max-w-3xl">
                                Most businesses lose opportunities{" "}
                                <em>before the conversation even starts.</em>{" "}
                                Buyers decide minutes earlier — judging your
                                reputation through a handful of small details.
                            </p>
                        </Reveal>

                        <RevealStagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10">
                            {[
                                { t: "Outdated websites", d: "A digital storefront that quietly tells buyers your standards have slipped." },
                                { t: "Weak online presence", d: "Invisible on the searches that matter, forgettable on the ones you appear in." },
                                { t: "Poor trust signals", d: "No proof, no testimonials, no clarity — and a hesitation you can’t see." },
                                { t: "Inconsistent branding", d: "Every touchpoint contradicts the last. Confidence erodes silently." },
                                { t: "Low enquiry rates", d: "Visitors arrive, glance, and leave — to a competitor who looked more serious." },
                                { t: "No system, no growth", d: "Hope is a strategy that scales poorly. Results stay accidental." },
                            ].map((it, i) => (
                                <StaggerItem key={it.t}>
                                    <div className="border-t border-[#111111] pt-5 group">
                                        <div className="flex items-baseline justify-between">
                                            <p className="font-serif text-2xl text-[#111111] leading-tight">
                                                {it.t}
                                            </p>
                                            <span className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <p className="mt-3 text-[#444444] leading-relaxed">
                                            {it.d}
                                        </p>
                                    </div>
                                </StaggerItem>
                            ))}
                        </RevealStagger>
                    </div>
                </div>
            </Section>

            {/* ─── PILLAR FRAMEWORK (visual quadrant) ─── */}
            <Section className="bg-[#fafafa] border-y border-[#dddddd] relative overflow-hidden" data-testid="section-pillars">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center relative">
                    <div className="md:col-span-5">
                        <SectionLabel number="§ 03" label="The Framework" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                A complete
                                <br />
                                <em>presence system.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-8 text-[#444444] text-lg leading-relaxed max-w-md">
                                Four quiet forces — Presence, Trust,
                                Performance, Growth — engineered around your
                                business core. Each reinforces the next.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <Link
                                to="/services"
                                className="mt-10 inline-flex items-center gap-3 font-serif italic text-lg text-[#111111] link-underline"
                                data-testid="pillars-cta"
                            >
                                Explore the system →
                            </Link>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <PillarFramework />
                    </div>
                </div>
            </Section>

            {/* ─── SERVICES PREVIEW ─── */}
            <Section data-testid="section-services">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                    <div>
                        <SectionLabel number="§ 04" label="Services" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl">
                                Considered systems
                                <br />
                                <em>for serious businesses.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <Link
                            to="/services"
                            data-testid="services-view-all"
                            className="link-underline font-serif italic text-xl text-[#111111] self-start md:self-end"
                        >
                            View all ten services →
                        </Link>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {SERVICES.slice(0, 6).map((s) => (
                        <ServiceCard key={s.n} service={s} />
                    ))}
                </div>
            </Section>

            {/* ─── WHY PIXEMANCE (dark, infographic) ─── */}
            <Section className="bg-[#111111] text-white relative overflow-hidden" data-testid="section-why">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 relative">
                    <div className="md:col-span-5">
                        <SectionLabel number="§ 05" label="Why Pixemance" variant="light" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                Quiet work,
                                <br />
                                <em className="text-white/60">loud outcomes.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-md">
                                Every system we ship is engineered to
                                compound. From the first impression to the
                                tenth enquiry of the month — each step earns
                                the next.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-12 grid grid-cols-3 border-l border-t border-white/15 max-w-md">
                                {[
                                    { v: "12+", l: "Industries" },
                                    { v: "94%", l: "Retention" },
                                    { v: "4–8w", l: "Engagement" },
                                ].map((s) => (
                                    <div
                                        key={s.l}
                                        className="border-r border-b border-white/15 p-5"
                                    >
                                        <p className="font-serif text-3xl">
                                            {s.v}
                                        </p>
                                        <p className="mt-2 text-[10px] tracking-[0.28em] uppercase text-white/60">
                                            {s.l}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <WhyInfographic />
                    </div>
                </div>
            </Section>

            {/* ─── PROCESS JOURNEY ─── */}
            <Section data-testid="section-process">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-5">
                        <SectionLabel number="§ 06" label="Process" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                A clear,
                                <br />
                                <em>well-rehearsed process.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7 flex md:items-end">
                        <Reveal delay={0.1}>
                            <p className="text-[#444444] text-lg leading-relaxed max-w-xl">
                                Five quiet stages, each building on the last.
                                No theatre — just a calm path from
                                understanding your business to a presence that
                                earns enquiries.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <ProcessJourney />
            </Section>

            {/* ─── CASE STUDY TEASER ─── */}
            <Section className="bg-[#fafafa] border-y border-[#dddddd] relative overflow-hidden" data-testid="section-case-teaser">
                <FloatingMotif />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 relative">
                    <div className="md:col-span-6">
                        <SectionLabel number="§ 07" label="Selected Study" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                {CASE_STUDIES[0].title.split("—")[0].trim()}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 font-serif italic text-2xl text-[#444444]">
                                {CASE_STUDIES[0].outcome}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-[#444444] text-lg leading-relaxed max-w-md">
                                {CASE_STUDIES[0].body}
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-8 flex flex-wrap gap-2">
                                {CASE_STUDIES[0].tags.map((t) => (
                                    <span
                                        key={t}
                                        className="text-[10px] tracking-[0.28em] uppercase border border-[#111111] px-3 py-1.5"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-10">
                                <MagneticButton to="/work" testid="case-teaser-cta">
                                    View all case studies
                                </MagneticButton>
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6">
                        <CaseStudyVisual metrics={CASE_STUDIES[0].metrics} />
                    </div>
                </div>
            </Section>

            {/* ─── TESTIMONIALS ─── */}
            <Section data-testid="section-testimonials">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <SectionLabel number="§ 08" label="Testimonials" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                What partners
                                <br />
                                <em>quietly say.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.08}>
                                <figure
                                    data-testid={`testimonial-${i}`}
                                    className="bg-white border border-[#dddddd] p-8 md:p-10 h-full hover:border-[#111111] hover:-translate-y-1 transition-all duration-700"
                                >
                                    <div className="flex items-center justify-between">
                                        <span aria-hidden className="font-serif text-6xl leading-none text-[#dddddd]">“</span>
                                        <span className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                            № {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <blockquote className="mt-2 font-serif text-xl md:text-2xl text-[#111111] leading-snug">
                                        {t.quote}
                                    </blockquote>
                                    <figcaption className="mt-8 pt-5 border-t border-[#dddddd]">
                                        <p className="font-medium text-[#111111] text-sm">{t.name}</p>
                                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444] mt-1">
                                            {t.role} · {t.company}
                                        </p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ─── STATS (animated counters) ─── */}
            <Section data-testid="section-stats" className="py-20 md:py-28 bg-white">
                <SectionLabel number="§ 09" label="By the numbers" />
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#dddddd]">
                    <RevealStat value={12} suffix="+" label="Industries served" />
                    <RevealStat value={94} suffix="%" label="Client retention" />
                    <RevealStat value={437} suffix="%" label="Top enquiry uplift" />
                    <RevealStat value={4.9} decimals={1} suffix="★" label="Average review score" />
                </div>
            </Section>

            {/* ─── FAQ ─── */}
            <Section className="bg-[#fafafa] border-y border-[#dddddd]" data-testid="section-faq">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <SectionLabel number="§ 10" label="FAQ" />
                        <Reveal>
                            <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                Honest
                                <br />
                                <em>answers.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-8 text-[#444444] text-lg leading-relaxed max-w-md">
                                The questions serious business owners tend to
                                ask before the first call — answered the way
                                we would in the room.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <FaqList items={FAQS} />
                    </div>
                </div>
            </Section>

            <FinalCTA />
        </div>
    );
}
